export type NewsLocale =
    | "RU"
    | "KK"
    | "QQ"
    | "EN";

export type AdminNewsLocale =
    | "RU"
    | "KK"
    | "EN";

export type NewsFile = {
    fileUid: string;
    contentType?: string;
    fileName?: string;
    fileSize?: number;
};

export type NewsItem = {
    id: number;

    userId?: number;

    locale?: NewsLocale | string;

    title: string;

    content: string;

    imageUrls?: string[];

    videoUrls?: string[];

    files?: NewsFile[];

    createdAt?: string;

    updatedAt?: string;
};

export type NewsResponse = {
    content: NewsItem[];

    totalElements: number;

    totalPages: number;

    number: number;

    size: number;

    first?: boolean;

    last?: boolean;
};

export type NewsTranslationPayload = {
    locale: AdminNewsLocale;

    title: string;

    content: string;
};

export type CreateNewsPayload = {
    translations: NewsTranslationPayload[];

    videoUrls: string[];
};

/**
 * POST и PUT используют
 * одинаковый JSON-контракт.
 */
export type EditNewsPayload =
    CreateNewsPayload & {
        /**
         * Оставляем optional
         * для совместимости со старым кодом.
         */
        imageUrls?: string[];
    };

export type BackendFieldErrors =
    Record<
        string,
        string | string[]
    >;

export type BackendErrorResponse = {
    code?: string;

    message?: string;

    errorMessage?: string;

    fieldErrors?:
        | BackendFieldErrors
        | Array<{
              field?: string;
              fieldName?: string;
              message?: string;
          }>;
};

type Locale =
    | "KK"
    | "RU"
    | "EN";

type ApplyNewsBackendErrorsArgs = {
    error: any;

    form: any;

    setActiveLocale: (
        locale: Locale
    ) => void;

    setContentErrors: (
        value: Record<
            Locale,
            string
        >
    ) => void;
};

const normalizeFieldError = (
    value: unknown
): string => {
    if (Array.isArray(value)) {
        return value
            .filter(Boolean)
            .join(", ");
    }

    return typeof value === "string"
        ? value
        : "";
};

/**
 * Разбирает backend fieldErrors
 * и раскладывает ошибки
 * по нужным полям формы.
 */
export const applyNewsBackendErrors = ({
    error,
    form,
    setActiveLocale,
    setContentErrors,
}: ApplyNewsBackendErrorsArgs):
    | string
    | null => {
    const data: BackendErrorResponse =
        error?.response?.data || {};

    const contentErrors: Record<
        Locale,
        string
    > = {
        KK: "",
        RU: "",
        EN: "",
    };

    const formErrors: Array<{
        name: (
            | string
            | number
        )[];
        errors: string[];
    }> = [];

    let firstLocale:
        | Locale
        | null = null;

    const applyField = (
        rawField: string,
        rawMessage: unknown
    ) => {
        const message =
            normalizeFieldError(
                rawMessage
            );

        if (
            !rawField ||
            !message
        ) {
            return;
        }

        const field =
            rawField.toLowerCase();

        const locale: Locale | null =
    /(^|[.\[\]_-])kk($|[.\[\]_-])/i.test(rawField)
        ? "KK"
        : /(^|[.\[\]_-])ru($|[.\[\]_-])/i.test(rawField)
        ? "RU"
        : /(^|[.\[\]_-])en($|[.\[\]_-])/i.test(rawField)
        ? "EN"
        : null;
            /**
             * Ошибка контента CKEditor.
             */
        if (
            field.includes(
                "content"
            )
        ) {
            if (locale) {
                contentErrors[
                    locale
                ] = message;

                firstLocale =
                    firstLocale ||
                    locale;
            }

            return;
        }

        /**
         * Ошибка заголовка.
         */
        if (
            field.includes(
                "title"
            ) &&
            locale
        ) {
            formErrors.push({
                name: [
                    "translations",
                    locale,
                    "title",
                ],
                errors: [
                    message,
                ],
            });

            firstLocale =
                firstLocale ||
                locale;

            return;
        }

        /**
         * Ошибка видео.
         */
        if (
            field.includes(
                "videourls"
            ) ||
            field.includes(
                "video"
            )
        ) {
            formErrors.push({
                name: [
                    "videoUrls",
                ],
                errors: [
                    message,
                ],
            });
        }
    };

    const fieldErrors =
        data.fieldErrors;

    if (
        Array.isArray(
            fieldErrors
        )
    ) {
        fieldErrors.forEach(
            (item) =>
                applyField(
                    item.field ||
                        item.fieldName ||
                        "",
                    item.message ||
                        ""
                )
        );
    } else if (
        fieldErrors &&
        typeof fieldErrors ===
            "object"
    ) {
        Object.entries(
            fieldErrors
        ).forEach(
            ([
                field,
                message,
            ]) =>
                applyField(
                    field,
                    message
                )
        );
    }

    if (
        formErrors.length
    ) {
        form.setFields(
            formErrors
        );
    }

    setContentErrors(
        contentErrors
    );

    if (firstLocale) {
        setActiveLocale(
            firstLocale
        );
    }

    return (
        data.message ||
        data.errorMessage ||
        data.code ||
        null
    );
};